import { type SearchRequest, TypeAheadResponse, TypeAheadItem } from '@/objects/generated';
import type { SearchType } from '@/objects/generated/enums/SearchType';
import { CreateRegexpSearchPatternForSearchType } from '@/utilities/searchutils';

export class TypeAheadDataSource {
    public ResultsCache = new Map<string, TypeAheadResponse>();
    public ActiveResult: TypeAheadResponse | null = null;

    /**
     * Items that we will always show in the top of the list, event if they dont match the query. This is useful for things like "Create new event" or "Search for more events" that we want to show even if they dont match the query.
     */
    public StickyItems: TypeAheadItem[] | null = null;

    constructor(
        public SearchType: SearchType,
        public QueryFunction: (query: SearchRequest) => Promise<TypeAheadResponse>,
        public ResultHeaderText?: string,
        public MaxItems?: number,
        public hrefGetter?: (item: TypeAheadItem) => string | undefined,
    ) {}

    /**
     * This is used if we hit cached data.
     * Basically we want to further reduce the available items based on the query.
     * @param items Some items we want to filter
     * @param query The query we want to filter the items by
     * @returns A filtered list of the input items.
     */
    public FilterItemsBySearchPattern(items: TypeAheadItem[], query: string): TypeAheadItem[] {
        const pattern = CreateRegexpSearchPatternForSearchType(query, this.SearchType);

        return items.filter((item) => pattern.test(item.Text!));
    }

    /**
     * Adds the results to the cache
     * @param query The querystring that generated the results
     * @param results The results that we want to cache
     */
    private AddResultsToCache(query: string, results: TypeAheadResponse) {
        // Empty queries are not cached
        if (!query) {
            return;
        }

        // Dont set it if we already have it
        if (this.ResultsCache.has(query)) {
            return;
        }

        this.ResultsCache.set(query, results);
    }

    /**
     * Tries to do the query against the cache, if we cant then we do the actual {@link QueryFunction}
     * @param request The request supplied by the TypeAhead component
     * @returns A TypeAheadResponse
     */
    public async QueryCached(request: SearchRequest): Promise<TypeAheadResponse> {
        // If we dont have a query, we skip the cache
        if (!request.Query) {
            return this.QueryFunction(request);
        }

        // If we already have the exact query in the cache, return it
        if (this.ResultsCache.has(request.Query)) {
            return this.ResultsCache.get(request.Query)!;
        }

        // I'm not sure I trust this logic
        switch (this.SearchType) {
            case 'StartsWith':
                // If we already have "einarittet" and we search for "einar" the results will be "einarittet" filtered further, so we dont have to do the query again.
                // Do we have to check more than one key here? The first that matches should contain all the possible results right?
                for (const key of this.ResultsCache.keys()) {
                    if (key.startsWith(request.Query) && this.ResultsCache.get(key)!.Items.length < this.MaxItems!) {
                        return TypeAheadResponse.FromJsValues({
                            Items: this.FilterItemsBySearchPattern(this.ResultsCache.get(key)!.Items, request.Query),
                        });
                    }
                }
                break;

            case 'EndsWith':
                // If we already have "rittet" and we search for "einarittet" the results will be "rittet" filtered further, so we dont have to do the query again.
                // What about here? Do we need to look at more than one key?
                for (const key of this.ResultsCache.keys()) {
                    if (request.Query.endsWith(key) && this.ResultsCache.get(key)!.Items.length < this.MaxItems!) {
                        return TypeAheadResponse.FromJsValues({
                            Items: this.FilterItemsBySearchPattern(this.ResultsCache.get(key)!.Items, request.Query),
                        });
                    }
                }
                break;

            case 'ExactWord': // This can never happen because if we have exact match then we have already matched on the key.
            case 'Contains': // We probably cant figure this out without doing the query.
                break;
        }

        // Note: We only cache if we run the QueryFunction. If we hit the cache above, then we dont cache the result since the result is built from the already cached data.
        return this.QueryFunction(request).then((results) => {
            this.AddResultsToCache(request.Query!, results);
            return results;
        });
    }

    /**
     * Clears the cache if we have more items than the maxItems
     * @param maxItems The maximum number of items we want to cache
     */
    public ClearCacheIfFull(maxItems: number) {
        let totalItems = 0;

        for (const value of this.ResultsCache.values()) {
            if (value != undefined && value.Items != undefined) {
                totalItems += value.Items.length;
            }
        }

        if (totalItems > maxItems) {
            this.ResultsCache.clear();
        }
    }
}
